import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

interface InternshipSessionFormComponentProps {
  departmentNames: RefObject<Array<string>>;
  studyLineNames: RefObject<Array<Array<string>>>;
}

interface SessionValue {
  description?: string;
  yearsOfStudy?: number | null;
  courseCredit?: string;
  sessionHours?: number | null;
}

const isFilled = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== '';

const isComplete = (session: SessionValue): boolean =>
  isFilled(session?.description) &&
  isFilled(session?.yearsOfStudy) &&
  isFilled(session?.courseCredit) &&
  isFilled(session?.sessionHours);

export const InternshipSessionFormComponent = ({
  departmentNames,
  studyLineNames,
}: InternshipSessionFormComponentProps): ReactNode => {
  const { t } = useTranslation();
  const [selectedDepartment, setSelectedDepartment] = useState(0);
  const [selectedLine, setSelectedLine] = useState(0);

  const watchedSessions = Form.useWatch(['sessions'], {
    preserve: true,
  }) as SessionValue[][] | undefined;
  const sessions = watchedSessions ?? [];

  // Keep the existing flattened session index for every study line.
  const items = (studyLineNames.current ?? []).flatMap(
    (lines, departmentIndex) =>
      lines.map((studyLineName, lineIndex) => {
        const flatIndex =
          (studyLineNames.current ?? [])
            .slice(0, departmentIndex)
            .reduce(
              (count, departmentLines) => count + departmentLines.length,
              0
            ) + lineIndex;

        const lineSessions = sessions[flatIndex] ?? [];
        const hours = lineSessions.reduce(
          (sum, session) => sum + (Number(session?.sessionHours) || 0),
          0
        );

        return {
          departmentIndex,
          lineIndex,
          flatIndex,
          studyLineName,
          sessions: lineSessions,
          hours,
          complete: lineSessions.length > 0 && lineSessions.every(isComplete),
        };
      })
  );

  const departmentOptions = (departmentNames.current ?? []).map(
    (name, index) => ({
      value: index,
      label: name || '-',
    })
  );

  const departmentLines = items.filter(
    (item) => item.departmentIndex === selectedDepartment
  );

  const activeLineIndex = Math.min(
    selectedLine,
    Math.max(departmentLines.length - 1, 0)
  );
  const selectedItem = departmentLines[activeLineIndex];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 20 }}>
        <Typography.Text type="secondary">
          {t('uniForm.depComp.name')}
        </Typography.Text>
        <Select
          style={{ width: '100%', marginTop: 8 }}
          value={selectedDepartment}
          options={departmentOptions}
          onChange={(departmentIndex) => {
            setSelectedDepartment(departmentIndex);
            setSelectedLine(0);
          }}
        />
      </Card>

      {departmentLines.length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            {t('uniForm.studyLinesLabel')}
          </Typography.Title>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
              gap: 12,
              marginBottom: 24,
            }}
          >
            {departmentLines.map((item, index) => {
              const isSelected = index === activeLineIndex;

              return (
                <Card
                  key={`${item.departmentIndex}-${item.lineIndex}`}
                  size="small"
                  hoverable
                  onClick={() => setSelectedLine(index)}
                  style={{
                    cursor: 'pointer',
                    borderColor: isSelected ? '#1677ff' : undefined,
                    background: isSelected
                      ? 'rgba(22, 119, 255, 0.04)'
                      : undefined,
                  }}
                >
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text strong>
                      {item.studyLineName || '-'}
                    </Typography.Text>

                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {departmentNames.current?.[item.departmentIndex] || '-'}
                    </Typography.Text>

                    {item.sessions.length === 0 ? (
                      <Tag>{t('uniForm.masterDetail.noSessions')}</Tag>
                    ) : item.complete ? (
                      <Tag color="success">
                        {t('uniForm.masterDetail.sessionsSummary', {
                          count: item.sessions.length,
                          hours: item.hours,
                        })}
                      </Tag>
                    ) : (
                      <Tag color="warning">
                        {t('uniForm.masterDetail.incomplete')}
                      </Tag>
                    )}
                  </Space>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {selectedItem && (
        <>
          <div style={{ marginBottom: 20 }}>
            <Typography.Title level={4} style={{ marginBottom: 4 }}>
              {selectedItem.studyLineName || '-'}
            </Typography.Title>
            <Typography.Text type="secondary">
              {departmentNames.current?.[selectedDepartment] ?? ''}
              {' · '}
              {t('uniForm.sessionComp.label')}
            </Typography.Text>
          </div>

          <Form.List name={['sessions', selectedItem.flatIndex]}>
            {(fields, fieldOperations) => (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                  gap: 16,
                  alignItems: 'start',
                }}
              >
                {fields.map((field) => (
                  <Card
                    size="small"
                    key={field.key}
                    title={`${t('uniForm.sessionComp.label')} ${field.name + 1}`}
                    extra={
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label={`${t('uniForm.sessionComp.label')} ${field.name + 1}`}
                        onClick={() => fieldOperations.remove(field.name)}
                      />
                    }
                  >
                    <Form.Item
                      label={t('uniForm.sessionComp.description')}
                      tooltip={t('uniForm.help.sessionDescription')}
                      rules={[
                        {
                          required: true,
                          message: t('validator.sessionDescriptionEmpty'),
                        },
                      ]}
                      name={[field.name, 'description']}
                    >
                      <TextArea autoSize />
                    </Form.Item>

                    <Form.Item
                      label={t('uniForm.sessionComp.yearsOfStudy')}
                      tooltip={t('uniForm.help.yearsOfStudy')}
                      rules={[
                        {
                          required: true,
                          message: t('validator.sessionYearsOfStudyEmpty'),
                        },
                      ]}
                      name={[field.name, 'yearsOfStudy']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label={t('uniForm.sessionComp.courseCredit')}
                      tooltip={t('uniForm.help.courseCredit')}
                      rules={[
                        {
                          required: true,
                          message: t('validator.sessionCourseCreditEmpty'),
                        },
                      ]}
                      name={[field.name, 'courseCredit']}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label={t('uniForm.sessionComp.hours')}
                      tooltip={t('uniForm.help.sessionHours')}
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: t('validator.sessionHoursEmpty'),
                        },
                      ]}
                      name={[field.name, 'sessionHours']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Card>
                ))}

                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => fieldOperations.add()}
                  style={{ minHeight: 48 }}
                >
                  {t('uniForm.sessionComp.addSession')}
                </Button>
              </div>
            )}
          </Form.List>
        </>
      )}
    </div>
  );
};
